import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import Contact from './pages/Contact';
import MakeupLanding from './pages/MakeupLanding';
import Snowfall from './components/Snowfall';
import './App.css';

// Title component to handle dynamic page titles
function PageTitle({ title }) {
  useEffect(() => {
    document.title = title;
  }, [title]);
  return null;
}

// NotFound component
const NotFound = () => {
  return (
    <div className="not-found-container">
      <div className="not-found-content">
        <h1>Babe, you look lost...</h1>
        <Link to="/" className="home-button">
          Take me home
        </Link>
      </div>
    </div>
  );
};

// Flip to false to disable the seasonal snow overlay.
const SHOW_CHRISTMAS_SNOW = false;

function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const sliderRef = useRef(null);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const isDraggingRef = useRef(false);
  const autoRotateTimerRef = useRef(null);

  // Add scroll handler
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDragStart = (e) => {
    isDraggingRef.current = true;
    startXRef.current = e.type === 'mousedown' ? e.pageX : e.touches[0].pageX;
    scrollLeftRef.current = sliderRef.current.scrollLeft;
    setIsPaused(true);
  };

  const handleDragMove = (e) => {
    if (!isDraggingRef.current) return;
    e.preventDefault();
    const x = e.type === 'mousemove' ? e.pageX : e.touches[0].pageX;
    const walk = (x - startXRef.current);
    sliderRef.current.scrollLeft = scrollLeftRef.current - walk;
  };

  const handleDragEnd = () => {
    isDraggingRef.current = false;
    if (!sliderRef.current) return;
    const slideWidth = sliderRef.current.offsetWidth;
    const newSlide = Math.round(sliderRef.current.scrollLeft / slideWidth);
    setCurrentSlide(newSlide);
    sliderRef.current.scrollTo({
      left: newSlide * slideWidth,
      behavior: 'smooth'
    });

    // Resume auto-rotation after 5 seconds of no interaction
    setTimeout(() => setIsPaused(false), 5000);
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    // Set up event listeners
    slider.addEventListener('touchstart', handleDragStart);
    slider.addEventListener('touchmove', handleDragMove);
    slider.addEventListener('touchend', handleDragEnd);
    slider.addEventListener('mousedown', handleDragStart);
    slider.addEventListener('mousemove', handleDragMove);
    slider.addEventListener('mouseup', handleDragEnd);
    slider.addEventListener('mouseleave', handleDragEnd);

    // Set up auto-rotation
    autoRotateTimerRef.current = setInterval(() => {
      if (isPaused || !sliderRef.current) return;

      setCurrentSlide(previousSlide => {
        const nextIndex = (previousSlide + 1) % 3;
        sliderRef.current.scrollTo({
          left: nextIndex * sliderRef.current.offsetWidth,
          behavior: 'smooth'
        });
        return nextIndex;
      });
    }, 5000);

    return () => {
      // Clean up event listeners
      slider.removeEventListener('touchstart', handleDragStart);
      slider.removeEventListener('touchmove', handleDragMove);
      slider.removeEventListener('touchend', handleDragEnd);
      slider.removeEventListener('mousedown', handleDragStart);
      slider.removeEventListener('mousemove', handleDragMove);
      slider.removeEventListener('mouseup', handleDragEnd);
      slider.removeEventListener('mouseleave', handleDragEnd);

      // Clean up auto-rotation timer
      if (autoRotateTimerRef.current) {
        clearInterval(autoRotateTimerRef.current);
      }
    };
  }, [isPaused]);

  const handleDotClick = (index) => {
    if (!sliderRef.current) return;
    setCurrentSlide(index);
    setIsPaused(true);
    sliderRef.current.scrollTo({
      left: index * sliderRef.current.offsetWidth,
      behavior: 'smooth'
    });
    // Resume auto-rotation after 5 seconds
    setTimeout(() => setIsPaused(false), 5000);
  };

  return (
    <Router>
      <Routes>
        {/* Main routes */}
        <Route path="/" element={
          <>
            <PageTitle title="Makeup, Lashes & Aesthetics in Manchester | KTP Beauty Clinic" />
            <div className="App homepage">
              {SHOW_CHRISTMAS_SNOW && <Snowfall />}
              <header className={`App-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="left-section">
                  <Link to="/" className="logo-link">
                    <img src="/images/headerlogo.png" alt="Logo" className="logo" />
                  </Link>
                  <nav className="main-nav">
                    <Link to="/" className="nav-link">Home</Link>
                    <details className="nav-dropdown">
                      <summary className="nav-link dropdown-toggle">Services</summary>
                      <div className="dropdown-menu">
                        <Link to="/makeup-services" className="dropdown-link">Makeup</Link>
                      </div>
                    </details>
                    <a
                      href="https://app.acuityscheduling.com/catalog.php?owner=19786092&category=Gift+Certificates"
                      className="nav-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Gift Cards
                    </a>
                    <Link to="/contact" className="nav-link">Contact</Link>
                  </nav>
                </div>
                <div className="right-section">
                  <a href="https://app.acuityscheduling.com/schedule/fe86af80" target="_blank" rel="noopener noreferrer" className="book-now">Book Now</a>
                  <a href="https://www.instagram.com/ktpbeautyclinic/" target="_blank" rel="noopener noreferrer" className="instagram-link">
                    <img src="/images/instagram.svg" alt="Instagram" />
                  </a>
                </div>
                <div className="mobile-icons">
                  <Link to="/contact" className="icon-link" aria-label="Message us">
                    <img src="/images/message-icon.svg" alt="" className="icon" />
                  </Link>
                  <a href="https://app.acuityscheduling.com/schedule/fe86af80" target="_blank" rel="noopener noreferrer" className="icon-link">
                    <img src="/images/calendar-icon.svg" alt="Book" className="icon" />
                  </a>
                  <a href="https://www.instagram.com/ktpbeautyclinic/" target="_blank" rel="noopener noreferrer" className="icon-link">
                    <img src="/images/instagram.svg" alt="Instagram" className="icon" />
                  </a>
                  <a href="tel:+44161494392" className="icon-link">
                    <img src="/images/phone-icon.svg" alt="Call Us" className="icon" />
                  </a>
                </div>
              </header>

              <div className="hero-section">
                <div className="hero-content">
                  <h2>KTP BEAUTY CLINIC</h2>
                  <h1>Beauty,<br />it's what<br />we do</h1>
                  <p className="services">Lashes • Makeup • Nails • Aesthetics • Semi-Perm Brows • Facials • Massage • Wedding Hair & Makeup</p>
                  <div className="hero-cta-group">
                    <a
                      href="https://app.acuityscheduling.com/schedule/fe86af80"
                      className="cta-button"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      BOOK AN APPOINTMENT
                    </a>
                    <a
                      href="https://app.acuityscheduling.com/catalog.php?owner=19786092&category=Gift+Certificates"
                      className="cta-button outline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      BUY A GIFT CERTIFICATE
                    </a>
                  </div>
                  <p style={{ marginTop: '15px', fontSize: '1rem', opacity: '0.9' }}>
                    Or call us on <a href="tel:+441614946392" style={{ color: 'inherit', textDecoration: 'underline' }}>0161 494 6392</a>
                  </p>
                </div>
              </div>

              <div className="about-section">
                <div className="about-content">
                  <div className="about-text">
                    <h2>About Us</h2>
                    <p>I'm Katie, founder of KTP Beauty Clinic. I've been in the beauty industry for over ten years and have run my salon for the past seven. From day one, it's been about helping people feel confident, comfortable and cared for.</p>
                    <p>We're now a team of seven amazing professionals, and I'm so proud of what we've built together. Alongside our full range of services, we're an accredited training centre for semi-permanent lashes and supply our own Lift Lashes to salons across the country.</p>
                    <p>Whether it's your everyday glow or your big day moment, I got you.</p>
                  </div>
                  <div className="about-image">
                    <img src="/images/katie.jpg" alt="Katie at KTP Beauty Clinic" />
                  </div>
                </div>
              </div>

              <section className="testimonials-section">
                <div className="testimonials-container">
                  <h2>Don't just take our word for it</h2>
                  <div
                    className="testimonials-slider"
                    ref={sliderRef}
                  >
                    <div className="testimonial">
                      <p className="testimonial-text">"If you want to get everything done in one place, this is it. Hair, nails make-up, brows, lashes, filler. There's something for everyone, all the girls are really nice would definitely recommend."</p>
                      <div className="testimonial-stars">★★★★★</div>
                      <p className="testimonial-author">Vicky Reynolds</p>
                      <p className="testimonial-source">TRUSTED REVIEW | GOOGLE LOCAL GUIDE</p>
                    </div>
                    <div className="testimonial">
                      <p className="testimonial-text">"Absolutely fab service was amazing girls soo lovely Katie's a beautiful bubble girl definitely recommended for lashes make up and hair and so much more"</p>
                      <div className="testimonial-stars">★★★★★</div>
                      <p className="testimonial-author">Kyrsty Chorlton</p>
                      <p className="testimonial-source">TRUSTED REVIEW | GOOGLE</p>
                    </div>
                    <div className="testimonial">
                      <p className="testimonial-text">"My daughter had eye lash extensions done here, they were beautiful. The staff are all really nice. They do hair, makeup, nails and eye lashes and brows"</p>
                      <div className="testimonial-stars">★★★★★</div>
                      <p className="testimonial-author">Hayley Henderson</p>
                      <p className="testimonial-source">TRUSTED REVIEW | GOOGLE LOCAL GUIDE</p>
                    </div>
                  </div>
                  <div className="testimonial-navigation">
                    {[0, 1, 2].map((index) => (
                      <button
                        key={index}
                        className={`nav-dot ${currentSlide === index ? 'active' : ''}`}
                        onClick={() => handleDotClick(index)}
                      />
                    ))}
                  </div>
                </div>
              </section>

              <section className="cta-section">
                <h2>What are you waiting for?</h2>
                <div className="cta-buttons">
                  <Link to="https://app.acuityscheduling.com/schedule/fe86af80" className="cta-section-button">Book Now</Link>
                  <a href="tel:+44161494392" className="cta-section-button outline">Call Us</a>
                </div>
              </section>

              <footer className="footer">
                <div className="footer-content">
                  <div className="footer-section">
                    <h4>Contact</h4>
                    <p>126 Higher Bents Ln<br />Bredbury, Stockport<br />SK6 2LU</p>
                    <p><a href="tel:+44161494392">+44 161 494 6392</a></p>
                  </div>

                  <div className="footer-section">
                    <h4>Opening Hours</h4>
                    <p>Our typical hours are listed below, but it's always best to check in before hand, or book an appointment online.</p>
                    <p>Monday - Saturday: 10am - 7pm<br />Sunday: Closed</p>
                  </div>

                  <div className="footer-section">
                    <h4>Reviews</h4>
                    <div className="rating">
                      <p>
                        <span className="stars">⭐⭐⭐⭐⭐</span>
                        <br />
                        <span className="rating-link">4.8 on Google Reviews</span>
                      </p>
                    </div>
                    <a href="https://www.instagram.com/ktpbeautyclinic/" className="social-link" target="_blank" rel="noopener noreferrer">
                      <img src="/images/instagram.svg" alt="Follow us on Instagram" />
                    </a>
                  </div>
                </div>
                <div className="footer-copyright">
                  <p>&copy; {new Date().getFullYear()} KTP Beauty Clinic. All rights reserved.</p>
                </div>
              </footer>
            </div>
          </>
        } />
        <Route path="/makeup-services" element={<MakeupLanding />} />
        <Route path="/contact" element={
          <>
            <PageTitle title="Contact Us - KTP Beauty Clinic" />
            <div className="App contact-page">
              <header className={`App-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="left-section">
                  <Link to="/" className="logo-link">
                    <img src="/images/headerlogo.png" alt="Logo" className="logo" />
                  </Link>
                  <nav className="main-nav">
                    <Link to="/" className="nav-link">Home</Link>
                    <details className="nav-dropdown">
                      <summary className="nav-link dropdown-toggle">Services</summary>
                      <div className="dropdown-menu">
                        <Link to="/makeup-services" className="dropdown-link">Makeup</Link>
                      </div>
                    </details>
                    <Link to="/contact" className="nav-link">Contact</Link>
                  </nav>
                </div>
                <div className="right-section">
                  <a href="https://app.acuityscheduling.com/schedule/fe86af80" target="_blank" rel="noopener noreferrer" className="book-now">Book Now</a>
                  <a href="https://www.instagram.com/ktpbeautyclinic/" target="_blank" rel="noopener noreferrer" className="instagram-link">
                    <img src="/images/instagram.svg" alt="Instagram" />
                  </a>
                </div>
                <div className="mobile-icons">
                  <Link to="/contact" className="icon-link" aria-label="Message us">
                    <img src="/images/message-icon.svg" alt="" className="icon" />
                  </Link>
                  <a href="https://app.acuityscheduling.com/schedule/fe86af80" target="_blank" rel="noopener noreferrer" className="icon-link">
                    <img src="/images/calendar-icon.svg" alt="Book" className="icon" />
                  </a>
                  <a href="https://www.instagram.com/ktpbeautyclinic/" target="_blank" rel="noopener noreferrer" className="icon-link">
                    <img src="/images/instagram.svg" alt="Instagram" className="icon" />
                  </a>
                  <a href="tel:+44161494392" className="icon-link">
                    <img src="/images/phone-icon.svg" alt="Call Us" className="icon" />
                  </a>
                </div>
              </header>
              <Contact />
            </div>
          </>
        } />
        <Route path="*" element={
          <>
            <PageTitle title="404 - Page Not Found - KTP Beauty Clinic" />
            <div className="App">
              <header className={`App-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="left-section">
                  <Link to="/" className="logo-link">
                    <img src="/images/headerlogo.png" alt="Logo" className="logo" />
                  </Link>
                  <nav className="main-nav">
                    <Link to="/" className="nav-link">Home</Link>
                    <details className="nav-dropdown">
                      <summary className="nav-link dropdown-toggle">Services</summary>
                      <div className="dropdown-menu">
                        <Link to="/makeup-services" className="dropdown-link">Makeup</Link>
                      </div>
                    </details>
                    <Link to="/contact" className="nav-link">Contact</Link>
                  </nav>
                </div>
                <div className="right-section">
                  <a href="https://app.acuityscheduling.com/schedule/fe86af80" target="_blank" rel="noopener noreferrer" className="book-now">Book Now</a>
                  <a href="https://www.instagram.com/ktpbeautyclinic/" target="_blank" rel="noopener noreferrer" className="instagram-link">
                    <img src="/images/instagram.svg" alt="Instagram" />
                  </a>
                </div>
                <div className="mobile-icons">
                  <Link to="/contact" className="icon-link" aria-label="Message us">
                    <img src="/images/message-icon.svg" alt="" className="icon" />
                  </Link>
                  <a href="https://app.acuityscheduling.com/schedule/fe86af80" target="_blank" rel="noopener noreferrer" className="icon-link">
                    <img src="/images/calendar-icon.svg" alt="Book" className="icon" />
                  </a>
                  <a href="https://www.instagram.com/ktpbeautyclinic/" target="_blank" rel="noopener noreferrer" className="icon-link">
                    <img src="/images/instagram.svg" alt="Instagram" className="icon" />
                  </a>
                  <a href="tel:+44161494392" className="icon-link">
                    <img src="/images/phone-icon.svg" alt="Call Us" className="icon" />
                  </a>
                </div>
              </header>
              <NotFound />
              <footer className="footer">
                <div className="footer-content">
                  <div className="footer-section">
                    <h4>Contact</h4>
                    <p>126 Higher Bents Ln<br />Bredbury, Stockport<br />SK6 2LU</p>
                    <p><a href="tel:+44161494392">+44 161 494 6392</a></p>
                  </div>

                  <div className="footer-section">
                    <h4>Opening Hours</h4>
                    <p>Our typical hours are listed below, but it's always best to check in before hand, or book an appointment online.</p>
                    <p>Monday - Saturday: 10am - 7pm<br />Sunday: Closed</p>
                  </div>

                  <div className="footer-section">
                    <h4>Reviews</h4>
                    <div className="rating">
                      <p>
                        <span className="stars">⭐⭐⭐⭐⭐</span>
                        <br />
                        <span className="rating-link">4.8 on Google Reviews</span>
                      </p>
                    </div>
                    <a href="https://www.instagram.com/ktpbeautyclinic/" className="social-link" target="_blank" rel="noopener noreferrer">
                      <img src="/images/instagram.svg" alt="Follow us on Instagram" />
                    </a>
                  </div>
                </div>
                <div className="footer-copyright">
                  <p>&copy; {new Date().getFullYear()} KTP Beauty Clinic. All rights reserved.</p>
                </div>
              </footer>
            </div>
          </>
        } />
      </Routes>
    </Router>
  );
}

export default App;
